import { Link } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { useUsers } from "@/api/queries";
import type { UserFilters, UserRow } from "@/api/types";
import { StatusBadge } from "@/components/feedback/status-badge";
import { OrgLink } from "@/components/navigation/entity-links";
import { AdminTable, type Column } from "@/components/tables/admin-table";
import { CopyIdsButton } from "@/components/tables/copy-ids";
import { type ResourceTableProps, tablePaging } from "@/components/tables/types";
import { formatDate, formatRelative } from "@/lib/dates";
import { formatPhone } from "@/lib/formatters";
import { ROLE_LABEL, USER } from "@/lib/status";

export function UsersTable({ query, onChange, toolbar, hide = [], onRowClick, filtered }: ResourceTableProps<UserFilters>) {
  const q = useUsers(query);
  const columns: Column<UserRow>[] = [
    {
      id: "user",
      header: "User",
      sortKey: "name",
      alwaysVisible: true,
      cell: (u) => (
        <Link to="/users/$userId" params={{ userId: u.id }} className="font-medium hover:underline">
          {u.name}
        </Link>
      ),
    },
    { id: "email", header: "Email", sortKey: "email", cell: (u) => <span className="font-mono text-[11.5px]">{u.email}</span> },
    { id: "phone", header: "Phone", cell: (u) => <span className="font-mono text-[11.5px]">{formatPhone(u.phone)}</span> },
    {
      id: "organization",
      header: "Organization",
      cell: (u) =>
        u.adminRole ? (
          <span className="text-muted-foreground">Rogue staff</span>
        ) : (
          <span className="flex flex-wrap gap-x-1.5">
            {u.organizations.map((o) => (
              <OrgLink key={o.id} org={o} />
            ))}
          </span>
        ),
    },
    {
      id: "stores",
      header: "Stores",
      sortKey: "storeCount",
      align: "right",
      cell: (u) => (u.storeCount > 1 ? <span title="Member of several stores">{u.storeCount} ★</span> : u.storeCount),
    },
    { id: "roles", header: "Roles", cell: (u) => <span className="text-xs">{u.roles.map((r) => ROLE_LABEL[r] ?? r).join(", ") || "—"}</span> },
    {
      id: "lastActive",
      header: "Last active",
      sortKey: "lastActiveAt",
      cell: (u) => <span className="text-muted-foreground">{u.lastActiveAt ? formatRelative(u.lastActiveAt) : "Never"}</span>,
    },
    { id: "status", header: "Status", cell: (u) => <StatusBadge config={USER[u.status]} /> },
    { id: "created", header: "Created", sortKey: "createdAt", defaultHidden: true, cell: (u) => formatDate(u.createdAt) },
  ];
  return (
    <AdminTable
      id="users"
      label="Users"
      resourceName="users"
      columns={columns.filter((c) => !hide.includes(c.id))}
      rows={q.data?.items}
      rowKey={(u) => u.id}
      isPending={q.isPending}
      isFetching={q.isFetching}
      error={q.error}
      onRetry={() => void q.refetch()}
      {...tablePaging(query, q.data?.total, onChange)}
      onRowClick={onRowClick ? (u) => onRowClick(u.id) : undefined}
      selectable
      bulkActions={(rows) => <CopyIdsButton ids={rows.map((r) => r.id)} />}
      toolbar={toolbar}
      empty={
        filtered
          ? { icon: Users, title: "No users match", description: "Clear filters or broaden the search." }
          : { icon: Users, title: "No users", description: "People appear once they're invited to an organization." }
      }
    />
  );
}
